import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useSubscribeToDashboard } from '@modules/common/hooks';
import { Cron } from '@modules/common/types';
import {
  getLocalCronValueFromUTCCronValue,
  getSavedEmailsFromReportSchedule,
  getSlackChannelsFromReportSchedule,
} from '@modules/common/utils';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { SubscribeButton } from '@modules/subscribe/components/SubscribeButton';
import { SubscribeDrawerV2 } from '@modules/subscribe/components/SubscribeDrawerV2';
import { RemoveScheduleReportDialogContent } from '@modules/subscribe/components/SubscribeDrawerV2/RemoveScheduleReportDialogContent';
import { getIsSaveButtonDisabled } from '@modules/subscribe/components/SubscribeDrawerV2/utils';
import {
  REMOVE_SUBSCRIPTION_MODAL_DESCRIPTION,
  REMOVE_SUBSCRIPTION_MODAL_TITLE,
} from '@modules/subscribe/constants';
import { useDeleteScheduleReportSubscription } from '@modules/subscribe/hooks/useDeleteScheduleReportSubscription';
import {
  ArtefactType,
  ReportingType,
  SlackChannelInput,
} from 'generated/graphql';
import { useState } from 'react';
import { DashboardSubscribeDrawerContentHeader } from './DashboardSubscribeDrawerContentHeader';

export const DashboardSubscribeV3 = () => {
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const { openModal, closeModal } = useMuiModalContext();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const reportSchedule = dashboard?.reporting?.secondarySchedules?.find(
    (schedule) => schedule.reportType === ReportingType.DefaultReport,
  );

  const reportArtefact = {
    id: dashboard?.id ?? '',
    type: ArtefactType.Dashboard,
    token: dashboard?.viewToken ?? '',
  };

  const { subscribeToDashboard, isLoading: isSavingSubscription } =
    useSubscribeToDashboard({
      reportSchedule,
      reportArtefact,
      skipPDFInReport: false,
      shouldRefetchQueries: true,
    });

  const { deleteScheduleReport, isLoading: isRemovingSubscription } =
    useDeleteScheduleReportSubscription({
      token: reportArtefact.token,
      shouldRefetchQueries: true,
    });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [cronSchedule, setCronSchedule] = useState<Cron | undefined>({
    realtime: reportSchedule?.cron.realtime,
    valueV2: getLocalCronValueFromUTCCronValue(reportSchedule?.cron.valueV2),
  });

  const savedSlackChannels = getSlackChannelsFromReportSchedule(reportSchedule);

  const [selectedSlackChannels, setSelectedSlackChannels] =
    useState<SlackChannelInput[]>(savedSlackChannels);

  const [selectedEmails, setSelectedEmails] = useState<string[]>(
    getSavedEmailsFromReportSchedule(reportSchedule),
  );

  const openDrawer = () => setIsDrawerOpen(true);

  const closeDrawer = () => setIsDrawerOpen(false);

  const handleSelectedSlackChannelsChange = (
    slackChannels: SlackChannelInput[],
  ) => {
    setSelectedSlackChannels(slackChannels);
  };

  const onSelectedEmailsChange = (emails: string[]) => {
    setSelectedEmails(emails);
  };

  const onSaveSubscription = async () => {
    if (!cronSchedule) {
      return;
    }

    try {
      await subscribeToDashboard({
        cron: cronSchedule,
        emails: selectedEmails,
        slackChannels: selectedSlackChannels,
      });
      showSuccessNotification({
        contentComponent: `Your subscription has been ${
          reportSchedule ? 'updated' : 'added'
        }.`,
        props: {},
      });
      trackDashboardEvent({
        eventName: isSubscribed
          ? 'Dashboard Subscribe - Subscription Updated'
          : 'Dashboard Subscribe - Subscription Added',
        dashboardLink: window.location.href,
        slackChannels: selectedSlackChannels,
        emails: selectedEmails,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to add the subscription.',
        props: {},
      });
    } finally {
      closeDrawer();
    }
  };

  const removeSubscription = async () => {
    if (!reportSchedule) {
      return;
    }
    try {
      closeModal();
      await deleteScheduleReport(reportSchedule.id);
      showSuccessNotification({
        contentComponent: 'The subscription has been removed.',
        props: {},
      });
      trackDashboardEvent({
        eventName: 'Dashboard Subscribe - Subscription Removed',
        dashboardLink: window.location.href,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to remove the subscription.',
        props: {},
      });
    } finally {
      closeDrawer();
    }
  };

  const openRemoveSubscriptionDialog = () => {
    openModal({
      contentComponent: () => (
        <RemoveScheduleReportDialogContent
          onClose={() => closeModal()}
          title={REMOVE_SUBSCRIPTION_MODAL_TITLE}
          description={REMOVE_SUBSCRIPTION_MODAL_DESCRIPTION}
          onRemove={removeSubscription}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  };

  const isSubscribed = !!reportSchedule;

  const isSaveButtonDisabled = getIsSaveButtonDisabled({
    reportSchedule,
    cronSchedule,
    selectedEmails,
    selectedSlackChannels,
    isPDFReportingIncluded: true,
  });

  if (!dashboard || !dashboard.id) {
    return null;
  }

  return (
    <>
      <SubscribeButton
        label="Subscribe"
        isDisabled={false}
        isSubscribed={isSubscribed}
        onClick={openDrawer}
        isLoading={false}
      />
      <SubscribeDrawerV2
        isOpen={isDrawerOpen}
        onOpen={openDrawer}
        onClose={closeDrawer}
        isSubscribed={isSubscribed}
        isSaveButtonDisabled={isSaveButtonDisabled}
        onSaveSubscription={onSaveSubscription}
        isSubscribeRequestInProgress={isSavingSubscription}
        onUnsubscribeButtonClick={openRemoveSubscriptionDialog}
        isUnsubscribeRequestInProgress={isRemovingSubscription}
        selectedSlackChannels={selectedSlackChannels}
        onSelectedSlackChannelsChange={handleSelectedSlackChannelsChange}
        selectedEmails={selectedEmails}
        onSelectedEmailsChange={onSelectedEmailsChange}
        savedCronValue={{
          realtime: reportSchedule?.cron.realtime,
          valueV2: getLocalCronValueFromUTCCronValue(
            reportSchedule?.cron.valueV2,
          ),
        }}
        cronSchedule={cronSchedule}
        setCronSchedule={setCronSchedule}
        headerNode={<DashboardSubscribeDrawerContentHeader />}
        removeSubscription={
          reportSchedule
            ? () => deleteScheduleReport(reportSchedule.id)
            : undefined
        }
        savedSlackChannels={savedSlackChannels}
      />
    </>
  );
};
